import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Router, RouterStateSnapshot, ActivatedRoute, RouterState } from '@angular/router';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly EXPIRATION_KEY = 'token_expiration';

  private isLoggedIn$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private information$: BehaviorSubject<object> = new BehaviorSubject<object>({});

  constructor(
    private router: Router,
    private http: HttpClient,
    private route: ActivatedRoute
  ) { }


  login(base64: any) {
    sessionStorage.setItem('token', JSON.stringify(base64));
    this.isLoggedIn$.next(true);
  }

  logout() {
    sessionStorage.clear();
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.EXPIRATION_KEY);
    this.isLoggedIn$.next(false);
    this.router.navigate(['']);
  }

  get isLogged$() {
    const token = sessionStorage.getItem('token');
    let possuiLGPD = this.possuiLGPD === '1' ? true : false;
    if (!token) {
      this.isLoggedIn$.next(false);
    } else if (token && !possuiLGPD) {
      this.isLoggedIn$.next(false);
    } else {
      this.isLoggedIn$.next(true);
    }
    return this.isLoggedIn$.asObservable();
  }

  get info$() {
    this.information$.next(JSON.parse(sessionStorage.getItem('information')));
    return this.information$.asObservable();
  }

  isActive() {
    this.isLogged$.subscribe((data) => {
      if (data) {
        if (this.router.url === '/' || this.router.url === '') {
          this.router.navigate(['home']);
        }
      } else {
        this.router.navigate(['']);
      }
    });
  }

  get tenantID() {
    return sessionStorage.getItem('tenantId');
  }

  get nomeEmpresa() {
    return sessionStorage.getItem('nomeEmpresa');
  }

  set nomeEmpresa(nome: string) {
    sessionStorage.setItem('nomeEmpresa', nome);
  }

  get possuiLGPD() {
    return sessionStorage.getItem('possuiLGPD');
  }

  set possuiLGPD(nome: string) {
    sessionStorage.setItem('possuiLGPD', nome);
  }

  get isPJ() {
    return sessionStorage.getItem('pj') === 'true';
  }

  set isPJ(value: boolean) {
    sessionStorage.setItem('pj', String(value));
  }

  setTenantID(codigoGrupo: string, codigoFilial: string) {
    sessionStorage.setItem('tenantId', `${codigoGrupo},${codigoFilial}`);
  }

  setInfo(info: object) {
    sessionStorage.setItem('information', JSON.stringify(info));
  }

  get emailGestor() {
    return sessionStorage.getItem('emailGestor');
  }

  set emailGestor(email: string) {
    sessionStorage.setItem('emailGestor', email);
  }

  getToken() {
    const token = sessionStorage.getItem('token');
    return token;
  }

  getVersion() {
    return this.http.get('assets/VERSION', { responseType: 'text' });
  }

  generateToken(): Observable<any> {
    const headers = new HttpHeaders({
      username: 'usuarioapi',
      password: 'ZK;i0981#$;%fLPcx2'
    });

    return this.http.post(`${environment.baseURL}/rest/api/oauth2/v1/token?grant_type=password`, {}, { headers }).pipe(
      tap((tokenData: any) => this.storeTokens(tokenData))
    );
  }

  private storeTokens(tokenData: any): void {
    const expiration = Date.now() + tokenData.expires_in * 1000;
    localStorage.setItem(this.ACCESS_TOKEN_KEY, tokenData.access_token);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, tokenData.refresh_token);
    localStorage.setItem(this.EXPIRATION_KEY, expiration.toString());
  }

  getAccessToken(): string {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY) || '';
  }

  getRefreshToken(): string {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY) || '';
  }

  getTokenExpiration(): number {
    return Number(localStorage.getItem(this.EXPIRATION_KEY)) || 0;
  }

  isTokenExpired(): boolean {
    const expiration = this.getTokenExpiration();
    return Date.now() > expiration - 60000;
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return of(null);

    const url = `${environment.baseURL}/rest/api/oauth2/v1/token`;
    const params = new HttpParams()
      .set('grant_type', 'refresh_token')
      .set('refresh_token', refreshToken);

    return this.http.post(`${url}?${params.toString()}`, {}, {
      headers: new HttpHeaders({})
    }).pipe(
      tap((tokenData: any) => this.storeTokens(tokenData))
    );
  }
}
